import { Divider, Spin } from 'antd';
import { Tree } from '@maxtropy/components';
import styles from './index.module.scss';
import { useEffect, useState } from 'react';
import { Empty, Input } from '@maxtropy/components';
import { apiV2PowerRoomConfigPowerRoomTreePost } from '@maxtropy/device-customer-apis-v2';
import { WarningFilled } from '@ant-design/icons';
import { isNil } from 'lodash-es';
import { DataNode } from 'antd/es/tree';

interface Props {
  setRoomId: (v: number) => void;
  setRoomName: (v: string) => void;
  setIsConfigurationMonitoring: (v: boolean) => void;
}

interface NewDataNode extends DataNode {
  name: string;
  key: number;
  id: number;
  parentId: number;
  isAlarm: boolean;
  isConfigurationMonitoring: boolean;
  children?: NewDataNode[];
}

// 将 API 数据格式转换为树结构
const transformTreeData = (list: any[], parentId: number = 0): NewDataNode[] => {
  return list.map(item => ({
    name: item.roomName,
    id: item.roomId,
    key: item.roomId,
    parentId,
    isAlarm: item.isAlarm,
    isConfigurationMonitoring: item.isConfigurationMonitoring,
    children: item.children ? transformTreeData(item.children, item.roomId) : [],
  }));
};

// 根据新的报警数据，更新原始树结构中的 isAlarm 字段
const updateAlarmStatus = (oldList: NewDataNode[], newList: NewDataNode[]): NewDataNode[] => {
  const map = new Map(newList.map(item => [item.id, item]));

  const updateNode = (node: NewDataNode): NewDataNode => {
    const updated = map.get(node.id);
    return {
      ...node,
      isAlarm: updated?.isAlarm ?? node.isAlarm,
      children: node.children?.map(updateNode) ?? [],
    };
  };

  return oldList.map(updateNode);
};

// 获取目标节点的所有父级路径
const findParentPath = (data: NewDataNode[], targetKey: number, path: any[] = []): any[] | null => {
  for (let node of data) {
    const newPath = [...path, node];
    if (node.key === targetKey) return newPath;
    if (node.children) {
      const found = findParentPath(node.children, targetKey, newPath);
      if (found) return found;
    }
  }
  return null;
};

const StationTree: React.FC<Props> = ({ setRoomId, setRoomName, setIsConfigurationMonitoring }) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const stationId = urlSearchParams.get('stationId');
  const [data, setData] = useState<NewDataNode[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState<string>();
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);

  useEffect(() => {
    if (!stationId && !data) return;
    const path = findParentPath(data, Number(stationId?.split('-')[0]));
    if (!path) return;

    // path 里最后一个是自己
    const selfKey = path[path.length - 1];

    setSelectedKeys([selfKey?.key]);
    const { id, name, isConfigurationMonitoring } = selfKey;
    setRoomId(id);
    setRoomName(name);
    setIsConfigurationMonitoring(!!isConfigurationMonitoring);

    if (path.length > 1) {
      // 说明不是一级 → 展开父节点
      setExpandedKeys(path.map(i => i.key).slice(0, -1));
    }

    // 定位锚点（延时等待 DOM 渲染）
    setTimeout(() => {
      const el = document.querySelector(`.ant-tree-treenode[data-node-key="${selfKey}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [data, stationId]);

  // 初次加载树结构
  useEffect(() => {
    setLoading(true);
    apiV2PowerRoomConfigPowerRoomTreePost({})
      .then((res: any) => {
        const formatted = transformTreeData(res?.list ?? []);
        setData(formatted);
        if (formatted?.[0]) {
          const { id, name, isConfigurationMonitoring } = formatted[0];
          setRoomId(id);
          setRoomName(name);
          setIsConfigurationMonitoring(!!isConfigurationMonitoring);
          setSelectedKeys(!isNil(id) ? [id] : []);
          const expandKeys = formatted.map(first => first.id);
          setExpandedKeys(expandKeys);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // 定时刷新报警状态
  useEffect(() => {
    const timer = setInterval(() => {
      apiV2PowerRoomConfigPowerRoomTreePost({})
        .then((res: any) => {
          const newFormatted = transformTreeData(res?.list ?? []);
          setData(prev => updateAlarmStatus(prev, newFormatted));
        })
        .catch(console.error);
    }, 20000);

    return () => clearInterval(timer);
  }, []);

  // 搜索时自动展开相关节点
  useEffect(() => {
    if (!searchVal) return;

    const matchedKeys: React.Key[] = [];
    const loop = (nodes: NewDataNode[]) => {
      for (const node of nodes) {
        if (node.name.includes(searchVal)) {
          matchedKeys.push(node.id);
        }
        if (node.children?.length) {
          loop(node.children);
        }
      }
    };
    loop(data);
    setExpandedKeys(matchedKeys);
    setAutoExpandParent(true);
  }, [searchVal, data]);

  return (
    <div className={styles.treeArea} style={{ width: '100%' }}>
      <div className={styles.searchArea}>
        <Input.Search placeholder="请输入名称" allowClear onSearch={v => setSearchVal(v)} />
        <Divider style={{ margin: '16px 0 0px' }} />
      </div>
      <div className={styles.treeBox}>
        <Spin spinning={loading}>
          {data.length > 0 ? (
            <Tree
              className={styles.treeStyle}
              blockNode
              treeData={data}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
              onExpand={keys => {
                setExpandedKeys(keys);
                setAutoExpandParent(false);
              }}
              onSelect={(keys, info) => {
                const nodeData = info.node as unknown as NewDataNode;
                setRoomId(nodeData.id);
                setRoomName(nodeData.name);
                setSelectedKeys(keys);
                setIsConfigurationMonitoring(!!nodeData.isConfigurationMonitoring);
              }}
              titleRender={(node: any) => {
                const strTitle = node.name;
                const searchName = searchVal ?? '';
                const index = strTitle.indexOf(searchName);

                return index > -1 && searchName ? (
                  <>
                    {strTitle.substring(0, index)}
                    <span style={{ color: 'red' }}>{searchName}</span>
                    {strTitle.slice(index + searchName.length)}
                    {node.isAlarm ? <WarningFilled style={{ marginLeft: 4, color: '#FAAD14' }} /> : null}
                  </>
                ) : (
                  <>
                    <span>{strTitle}</span>
                    {node.isAlarm ? <WarningFilled style={{ marginLeft: 4, color: '#FAAD14' }} /> : null}
                  </>
                );
              }}
            />
          ) : (
            <Empty style={{ marginTop: 40 }} />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default StationTree;
